import React, { useEffect, useRef, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import useStore from '../../store';
import { useLocation, useHistory } from 'react-router-dom';
import {
	Container,
	Box,
	Flex,
	Text,
	Center,
	Button,
	FormControl,
	FormLabel,
	Input,
	Select,
	Switch,
	useToast,
	Avatar,
	Wrap,
	WrapItem,
	useDisclosure,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import c from '../../constant';
import DownloadAndViewButton from '../../components/Files/DownloadAndViewButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusSquare, faSync, faTimes } from '@fortawesome/free-solid-svg-icons';
import SelectUserAllModal from '../../components/Modal/SelectUserShowAllModal';

const QUERY_DETAIL = gql`
	query get($id: String!) {
		getTrainingRecordById(id: $id) {
			id
			no
			courseTitle
			certificate
			organiser
			dateStart
			dateEnd
			location
			means
			type
			locationType
			attachmentFile
			bioListing
			updatedBy
			updatedAt
			user {
				id
				fullName
				fullNameKhmer
				avatar
			}
			updatedByUser {
				id
				username
				fullName
				fullNameKhmer
				department
				position
				avatar
			}
		}
	}
`;

const QUERY_USERS = gql`
	query getUsers($filterQuery: JSONObject, $offset: Float, $limit: Float, $name: String) {
		getUsers(filterQuery: $filterQuery, pagerInput: { limit: $limit, offset: $offset }, name: $name) {
			users {
				id
				username
				fullName
				fullNameKhmer
				createdAt
				role
				department
				office
				position
				active
				isTwoFactorEnabled
			}
		}
	}
`;

const MUTATION_UPDATE_TRAINING_RECORD = gql`
	mutation updateTrainingRecord(
		$id: String!
		$input: UpdateTrainingRecordInput!
		$attachmentFile: Upload
		$attachmentFileSize: Float
		$removeAttachmentFile: Boolean
	) {
		updateTrainingRecord(
			id: $id
			input: $input
			attachmentFile: $attachmentFile
			attachmentFileSize: $attachmentFileSize
			removeAttachmentFile: $removeAttachmentFile
		)
	}
`;

function TrainingRecordUpdate() {
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { t } = useTranslation();
	const location = useLocation();
	const history = useHistory();
	const toast = useToast();
	const cancelRef = useRef();
	const urlParams = new URLSearchParams(location.search);
	const id = urlParams.get('id') ? urlParams.get('id') : null;
	const { isOpen: isAttachmentModalOpen, onOpen: onAttachmentModalOpen, onClose: onAttachmentModalClose } = useDisclosure();
	const [attachmentFile, setAttachmentFile] = useState(null);
	const [currentAttachmentFile, setCurrentAttachmentFile] = useState(null);
	const [removeCurrentAttachmentFile, setRemoveCurrentAttachmentFile] = useState(false);
	const [officials, setOfficials] = useState(null);
	const [openSelectUserAllModel, setOpenSelectUserAllModel] = useState(false);
	const [loadDetail, { data, loading }] = useLazyQuery(QUERY_DETAIL, {
		fetchPolicy: 'no-cache',
	});
	const [form, setForm] = useState({
		userId: '',
		courseTitle: '',
		organiser: '',
		certificate: '',
		dateStart: '',
		dateEnd: '',
		location: '',
		means: '',
		type: '',
		locationType: '',
		bioListing: false,
	});
	const [loadUsers, { data: dataLoadUsers }] = useLazyQuery(QUERY_USERS, { fetchPolicy: 'no-cache' });
	const [updateTrainingRecord, { loading: loadingUpdate }] = useMutation(MUTATION_UPDATE_TRAINING_RECORD);

	useEffect(() => {
		if (id) {
			loadDetail({
				variables: {
					id,
				},
			});
		}
	}, [id]);

	useEffect(() => {
		loadUsers();
	}, []);

	useEffect(() => {
		const record = data?.getTrainingRecordById;

		if (record) {
			setForm({
				userId: record?.user?.id || '',
				courseTitle: record?.courseTitle || '',
				organiser: record?.organiser || '',
				certificate: record?.certificate || '',
				dateStart: record?.dateStart ? record.dateStart.substring(0, 10) : '',
				dateEnd: record?.dateEnd ? record.dateEnd.substring(0, 10) : '',
				location: record?.location || '',
				means: record?.means || '',
				locationType: record?.locationType || '',
				type: record?.type || '',
				bioListing: record?.bioListing === true,
			});

			setOfficials(record?.user || null);
			setCurrentAttachmentFile(record?.attachmentFile || null);
		}
	}, [data]);

	function handleChange(e) {
		const { name, value } = e.target;

		setForm((prev) => ({
			...prev,
			[name]: value,
		}));
	}

	function handleAttachmentChange(e) {
		const selectedFile = e.target.files?.[0];

		if (!selectedFile) return;

		setAttachmentFile(selectedFile);
		setRemoveCurrentAttachmentFile(false);

		e.target.value = '';
	}

	async function handleSubmit() {
		try {
			const variables = {
				id,
				input: {
					userId: form?.userId,
					courseTitle: form?.courseTitle,
					organiser: form?.organiser,
					certificate: form?.certificate,
					dateStart: form?.dateStart,
					dateEnd: form?.dateEnd,
					locationType: form?.locationType,
					location: form?.location,
					means: form?.means,
					type: form?.type,
					bioListing: form?.bioListing,
				},
				removeAttachmentFile: removeCurrentAttachmentFile,
			};

			if (attachmentFile) {
				variables.attachmentFile = attachmentFile;
				variables.attachmentFileSize = Number(attachmentFile.size);
				variables.removeAttachmentFile = false;
			}

			await updateTrainingRecord({
				variables,
			});

			toast({
				title: t('Training course updated successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
				duration: 3000,
			});

			history.push(`/training-course/detail?id=${id}`);
		} catch (error) {
			toast({
				title: t(error?.message || 'something went wrong'),
				status: 'error',
				isClosable: true,
				position: 'bottom-right',
				duration: 5000,
			});
		}
	}

	return (
		<Container maxW="container.lg" mt="16px" mb="16px">
			<BreadcrumbComponent
				list={[
					{
						name: 'Training Course',
						path: '/training-course',
					},
					{
						name: 'Detail',
						path: `/training-course/detail?id=${id}`,
					},
					{
						name: 'Update',
						path: '#',
					},
				]}
			/>

			<Center mt="5">
				<Box w="100%" bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)" pb="32px">
					<Flex alignItems="center" justifyContent="space-between" bg="#FAFAFA" p="16px" pt="8px" pb="8px" mb="16px" borderBottom="1px solid #dbdbdb">
						<Text fontSize="x-large" fontWeight="bold">
							{t('Update Training Record')}
						</Text>
					</Flex>

					<Box p="16px">
						{loading ? (
							<Text>{t('Loading')}</Text>
						) : (
							<>
								<FormControl mb="4">
									<FormLabel>{t('Officials')}</FormLabel>

									<Wrap>
										{officials && (
											<Flex direction="column" justifyContent="center" alignItems="center" mr={2}>
												<WrapItem className="avatarWraper" position="relative">
													<Avatar
														name={officials?.fullNameKhmer || officials?.fullName}
														src={officials?.avatar?.icon ? c.baseFilePath + officials?.avatar?.icon : undefined}
													/>
												</WrapItem>

												<Box mt={1}>
													<Text textAlign="center">{lang === 'kh' ? officials?.fullNameKhmer : officials?.fullName}</Text>
												</Box>
											</Flex>
										)}

										<Flex alignItems="start">
											<Button
												className="hidden-print"
												type="button"
												onClick={() => setOpenSelectUserAllModel(true)}
												leftIcon={<FontAwesomeIcon icon={faSync} style={{ fontSize: 16 }} />}
												colorScheme="blue"
												variant="outline"
											>
												{officials ? t('Change') : t('Add')}
											</Button>
										</Flex>
									</Wrap>

									{openSelectUserAllModel && (
										<SelectUserAllModal
											cached={false}
											onClose={() => setOpenSelectUserAllModel(false)}
											callback={(e) => {
												setOfficials(e);

												setForm((prev) => ({
													...prev,
													userId: e?.id || '',
												}));

												toast({
													title: t('You have changed a user'),
													status: 'success',
													isClosable: true,
													position: 'top-right',
												});

												setOpenSelectUserAllModel(false);
											}}
											viewType="all"
										/>
									)}
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Training Course Title')}</FormLabel>
									<Input name="courseTitle" value={form.courseTitle} onChange={handleChange} />
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Organiser Name')}</FormLabel>
									<Input name="organiser" value={form.organiser} onChange={handleChange} />
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Certificate')}</FormLabel>
									<Input name="certificate" value={form.certificate} onChange={handleChange} />
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('location')}</FormLabel>
									<Input name="location" value={form.location} onChange={handleChange} />
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Date From')}</FormLabel>
									<Input type="date" name="dateStart" value={form.dateStart} onChange={handleChange} />
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Date To')}</FormLabel>
									<Input type="date" name="dateEnd" value={form.dateEnd} onChange={handleChange} />
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Means')}</FormLabel>
									<Select name="means" value={form.means} onChange={handleChange}>
										<option value="">{t('Select')}</option>
										<option value="ONLINE">{t('Online')}</option>
										<option value="ONSITE">{t('ONSITE')}</option>
									</Select>
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Location Type')}</FormLabel>
									<Select name="type" value={form.type} onChange={handleChange}>
										<option value="">{t('Select')}</option>
										<option value="TRAINING">{t('Training')}</option>
										<option value="SEMINAR">{t('Seminar')}</option>
									</Select>
								</FormControl>

								<FormControl mb="4">
									<FormLabel>{t('Type')}</FormLabel>
									<Select name="locationType" value={form.locationType} onChange={handleChange}>
										<option value="">{t('Select')}</option>
										<option value="LOCAL">{t('Local')}</option>
										<option value="OVERSEAS">{t('Overseas')}</option>
									</Select>
								</FormControl>

								<FormLabel minW="175px" mb={0}>
									{t('Attachment File')}
								</FormLabel>

								{attachmentFile ? (
									<Flex alignItems="center" align="center" mt={3}>
										<Text mr={3} fontSize="16px">
											{attachmentFile.name}
										</Text>
										<Button
											size="sm"
											colorScheme="red"
											onClick={() => {
												setAttachmentFile(null);
											}}
										>
											<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />
										</Button>
									</Flex>
								) : currentAttachmentFile && !removeCurrentAttachmentFile ? (
									<Flex alignItems="center" mt={4}>
										<DownloadAndViewButton
											minW="150px"
											url={currentAttachmentFile?.file}
											size={currentAttachmentFile?.size}
											marginLeft={0}
										/>

										<Button
											size="sm"
											ml={3}
											colorScheme="red"
											children={<FontAwesomeIcon icon={faTimes} style={{ fontSize: 16 }} />}
											onClick={onAttachmentModalOpen}
										/>
									</Flex>
								) : null}

								<Box>
									<Button
										as="label"
										size="sm"
										mt={4}
										colorScheme="blue"
										rightIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 16 }} />}
										borderRadius={c.borderRadius}
									>
										{attachmentFile || (currentAttachmentFile && !removeCurrentAttachmentFile)
											? t('Change Attachment File')
											: t('Add Attachment File')}

										<input type="file" hidden onChange={handleAttachmentChange} />
									</Button>
								</Box>

								<FormControl display="flex" alignItems="center" mt={5} mb="4">
									<FormLabel mb="0">{t('List Course to Bio')}</FormLabel>
									<Switch
										isChecked={form.bioListing}
										onChange={(e) =>
											setForm((prev) => ({
												...prev,
												bioListing: e.target.checked,
											}))
										}
									/>
								</FormControl>

								<Flex justifyContent="flex-end" mt="6">
									<Button colorScheme="gray" mr="3" onClick={() => history.push(`/training-course/detail?id=${id}`)}>
										{t('Cancel')}
									</Button>

									<Button colorScheme="blue" isLoading={loadingUpdate} onClick={handleSubmit}>
										{t('Save')}
									</Button>
								</Flex>
							</>
						)}
					</Box>
				</Box>
			</Center>

			<AlertDialog isOpen={isAttachmentModalOpen} onClose={onAttachmentModalClose}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Remove Attachment')}
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text fontSize="15px">{t('Are you sure you want to remove this attachment? This action cannot be undone.')}</Text>
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={onAttachmentModalClose}>
								{t('Cancel')}
							</Button>
							<Button
								colorScheme="red"
								onClick={() => {
									setRemoveCurrentAttachmentFile(true);
									setAttachmentFile(null);
									onAttachmentModalClose();
								}}
								ml={3}
							>
								{t('Remove')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</Container>
	);
}

export default TrainingRecordUpdate;
